import React from 'react'
import { Box } from 'theme-ui'
import CarouselMarkers from './CarouselMarkers'

/**
 * CSS scroll-snap carousel.
 *
 * Replaces react-slick on this site. The trade-off, measured rather than
 * assumed:
 *
 *  - 0 kB of JavaScript. react-slick was a 61 kB chunk that was *prefetched on
 *    every page* even when no slider rendered (loadable() with
 *    webpackPrefetch: true), while the homepage hero was its only consumer.
 *  - Native momentum and rubber-banding on touch devices, because this is the
 *    browser's own scrolling rather than a JS-driven transform.
 *  - Works with JavaScript disabled, so the hero is never blank for a crawler
 *    or a failed hydration.
 *  - Keyboard accessible for free: a focused scroll container responds to
 *    arrow keys, Home and End without a key handler.
 *  - No autoplay. The previous implementation rotated every 3 s with no pause
 *    control, which fails WCAG 2.2.2 (Pause, Stop, Hide) — auto-advancing
 *    content running longer than 5 s needs a mechanism to pause it. User-driven
 *    scrolling removes the requirement entirely and is the better mobile
 *    pattern: people swipe when they want to look, rather than having a card
 *    slide away mid-read.
 *
 * Slides peek at the next one (92 % width on mobile) so the swipe affordance is
 * visible without dots or arrows.
 */
const CardListScroller = React.forwardRef(
  (
    {
      children,
      columns,
      ariaLabel,
      // Autoplay is opt-in and pausable. Callers pass `autoplay` (ms dwell) only
      // where motion is wanted; everything below exists to keep it WCAG 2.2.2
      // compliant rather than to make it move.
      autoplay = 0,
      autoplayLabels,
      ...rest
    },
    ref
  ) => {
  // Desktop columns come from the caller; mobile always shows one card plus a
  // peek so it is obvious the row scrolls.
  const desktopColumns = Array.isArray(columns)
    ? columns[columns.length - 1] || 1
    : columns || 1

  /**
   * A single-column row keeps a peek so it is obvious the row scrolls - at a
   * full 100 % the remaining slides sit entirely off-screen and unreachable.
   *
   * The peek is a fixed number of PIXELS, not a percentage. With a percentage
   * the peek shrinks as the screen grows, which is exactly what happened on a
   * phone in landscape: at the 94 % desktop basis an iPhone 15 Pro Max held an
   * 819 px card in an 871 px container and only 37 px of the next card showed -
   * the same ambiguity the portrait fix was meant to remove. A pixel basis keeps
   * the visible strip identical in both orientations.
   *
   * 96 px = 80 px of the next card plus the 12 px flex gap. 68 px was not
   * enough in landscape: the hero lays text left / image right above the 768 px
   * breakpoint, so a 55 px strip showed only blank white card surface and the
   * next card was still not identifiable.
   */
  const PEEK_PX = 96

  // Multi-column rows still need to show N cards, so they keep a percentage.
  const basis =
    desktopColumns > 1
      ? `${Math.floor(100 / desktopColumns) - 2}%`
      : `calc(100% - ${PEEK_PX}px)`

  const trackRef = React.useRef(null)
  const [active, setActive] = React.useState(0)

  /**
   * Autoplay state.
   *
   * `paused` is the user's explicit choice (the button, or any interaction).
   * `hovered` / `focused` / `offscreen` / `hidden` are temporary suspensions.
   * The timer only runs when none of them applies, which is what makes the
   * motion polite rather than something that keeps moving under the reader.
   */
  const [paused, setPaused] = React.useState(false)
  const [hovered, setHovered] = React.useState(false)
  const [focused, setFocused] = React.useState(false)
  const [offscreen, setOffscreen] = React.useState(false)
  const [docHidden, setDocHidden] = React.useState(false)
  const [reducedMotion, setReducedMotion] = React.useState(false)

  // Reduced motion: no autoplay at all, not merely a slower one.
  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const sync = () => setReducedMotion(mq.matches)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  // Pause while the tab is in the background - advancing a carousel nobody is
  // looking at is pure waste.
  React.useEffect(() => {
    if (typeof document === 'undefined') return undefined
    const sync = () => setDocHidden(document.hidden)
    sync()
    document.addEventListener('visibilitychange', sync)
    return () => document.removeEventListener('visibilitychange', sync)
  }, [])

  // Pause while the carousel is outside the viewport.
  React.useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined
    const track = trackRef.current
    if (!track) return undefined
    const io = new IntersectionObserver(
      entries => setOffscreen(!entries[0].isIntersecting),
      { threshold: 0.25 }
    )
    io.observe(track)
    return () => io.disconnect()
  }, [])

  const autoplayEnabled = Number(autoplay) > 0 && !reducedMotion
  const autoplayRunning =
    autoplayEnabled && !paused && !hovered && !focused && !offscreen && !docHidden

  const slideCount = React.Children.count(children)

  // Merge the caller's ref with our internal one: the ref is still forwarded so
  // existing call sites keep working.
  const setRefs = React.useCallback(
    node => {
      trackRef.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    },
    [ref]
  )

  const slideTo = React.useCallback(index => {
    const track = trackRef.current
    if (!track) return
    const slide = track.children[index]
    if (!slide) return
    // scrollIntoView respects scroll-snap and needs no offset maths.
    slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
  }, [])

  React.useEffect(() => {
    const track = trackRef.current
    if (!track) return undefined

    let frame = null
    const measure = () => {
      frame = null
      const slides = [...track.children]
      if (!slides.length) return
      // Nearest slide to the container's left edge is the current one.
      const left = track.scrollLeft
      let best = 0
      let bestDistance = Infinity
      slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - track.offsetLeft - left)
        if (distance < bestDistance) {
          bestDistance = distance
          best = index
        }
      })
      setActive(best)
    }

    const onScroll = () => {
      if (frame == null) frame = requestAnimationFrame(measure)
    }

    measure()
    track.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      track.removeEventListener('scroll', onScroll)
      if (frame != null) cancelAnimationFrame(frame)
    }
  }, [])

  // The timer. Restarts whenever `active` changes so the dwell is measured from
  // the slide becoming visible, not from page load.
  React.useEffect(() => {
    if (!autoplayRunning || slideCount < 2) return undefined
    const id = setTimeout(() => {
      const next = (active + 1) % slideCount
      slideTo(next)
    }, Number(autoplay))
    return () => clearTimeout(id)
  }, [autoplayRunning, active, slideCount, autoplay, slideTo])

  /**
   * Any deliberate interaction takes control away from the timer permanently.
   * WCAG 2.2.2 requires a pause mechanism; this goes further and treats "the
   * reader touched it" as "stop moving it" - a carousel that resumes on its own
   * after someone swiped is the behaviour the criterion exists to prevent.
   */
  const takeControl = React.useCallback(() => setPaused(true), [])

  const handleSelect = React.useCallback(
    index => {
      takeControl()
      slideTo(index)
    },
    [slideTo, takeControl]
  )

  return (
    <Box>
    <Box
      ref={setRefs}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={event => {
        // Only focus inside the track, not the browser focusing the page.
        if (trackRef.current && trackRef.current.contains(event.target)) {
          setFocused(true)
        }
      }}
      onBlur={event => {
        if (trackRef.current && !trackRef.current.contains(event.relatedTarget)) {
          setFocused(false)
        }
      }}
      onPointerDown={takeControl}
      onWheel={takeControl}
      onKeyDown={takeControl}
      // A scroll container needs to be reachable and operable without a mouse.
      // Cards contain links, so a browser will not make the container itself
      // keyboard-focusable; an explicit tabindex gives arrow-key scrolling.
      tabIndex={0}
      role='group'
      aria-roledescription='carousel'
      aria-label={ariaLabel}
      {...rest}
      sx={{
        display: 'flex',
        gap: 3,
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollSnapType: 'x mandatory',
        scrollBehavior: 'smooth',
        WebkitOverflowScrolling: 'touch',
        // Nothing to see in a scrollbar on a one-row scroller, and the peek
        // already signals more content.
        scrollbarWidth: 'none',
        '::-webkit-scrollbar': { display: 'none' },
        // Focus ring must stay visible for keyboard users.
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'alpha',
          outlineOffset: '4px'
        },
        '& > *': {
          /* A peek of the next card on every breakpoint. Without it the
             remaining slides are completely off-screen and nothing signals that
             they exist - on desktop 2 of 3 slides were invisible.

             The card wrappers are transparent (no background, no shadow, no
             radius) and the section behind them is #f8f8f8, so a narrow peek
             read as ambiguous background rather than as a card - at 92 % only
             17 px of the next card showed. */
          /* One basis at every breakpoint: the visible peek is then the same in
             portrait and in landscape, instead of shrinking as the viewport
             grows. */
          flex: `0 0 ${basis}`,
          /* The hero variant draws a decorative offset frame behind the photo
             (a `::before` block offset to the top-left). Inside a card that now
             has its own border and rounded corners, `overflow: hidden` clips
             that frame into a stray blue-grey strip along the top edge instead
             of letting it read as an offset layer. The card surface replaces it.
             `:has()` keeps the rule to the media link only. */
          '& a:has(> .gatsby-image-wrapper)::before': { display: 'none' },
          '& a:has(> .gatsby-image-wrapper)::after': { display: 'none' },
          scrollSnapAlign: 'start',
          scrollSnapStop: 'always',
          minWidth: 0,
          /* Give each slide a silhouette. The theme's hero card has no surface
             of its own, so the edge between card and page background was
             invisible - a soft shadow plus matching radius makes the card read
             as an object and the peek read as "another one of these". */
          borderRadius: 'default',
          overflow: 'hidden',
          /* A hairline border as well as the shadow: white cards on a #f8f8f8
             page have almost no edge contrast of their own, so the shadow alone
             was easy to miss on a bright phone screen in landscape. */
          border: '1px solid rgba(16, 24, 40, 0.08)',
          boxShadow: '0 2px 4px rgba(16, 24, 40, 0.08), 0 10px 32px rgba(16, 24, 40, 0.14)'
        },
        // Respect a user's reduced-motion preference: snap without the animated
        // glide.
        '@media (prefers-reduced-motion: reduce)': {
          scrollBehavior: 'auto'
        }
      }}
    >
      {children}
    </Box>
      <CarouselMarkers
        count={slideCount}
        active={active}
        onSelect={handleSelect}
        label={ariaLabel}
        autoplay={autoplayEnabled}
        paused={paused}
        onToggleAutoplay={() => setPaused(p => !p)}
        labels={autoplayLabels}
      />
    </Box>
  )
})

CardListScroller.displayName = 'CardListScroller'

export default CardListScroller
